import { useEffect, useState, useCallback } from 'react';
import { Terminal, RefreshCw, Loader2 } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { Button } from '../../components/ui/button';
import { getOutreachLogs } from '../../lib/outreach-api';

export const OutreachActivityLogsPage = () => {
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const data = await getOutreachLogs(100);
      setLogs(Array.isArray(data.data) ? data.data : []);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  return (
    <div className="p-6 lg:p-8 space-y-6" data-testid="outreach-activity-logs">
      <header className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <div className="flex items-center gap-2 mb-1">
            <Terminal className="w-4 h-4 text-amber-400" />
            <span className="text-xs uppercase tracking-widest text-amber-400 font-semibold">
              Outreach AI · Activity
            </span>
          </div>
          <h1 className="text-3xl font-semibold tracking-tight">Activity Logs</h1>
          <p className="text-sm text-muted-foreground mt-1">
            Every outbound message dispatched by the Outreach Agent, with intent and reply status.
          </p>
        </div>
        <Button
          variant="outline"
          size="sm"
          onClick={load}
          disabled={loading}
          data-testid="refresh-logs-btn"
        >
          {loading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <RefreshCw className="w-4 h-4 mr-2" />}
          Refresh
        </Button>
      </header>

      <Card>
        <CardHeader>
          <CardTitle className="text-base">Recent activity</CardTitle>
        </CardHeader>
        <CardContent className="space-y-2">
          {loading && logs.length === 0 && (
            <Loader2 className="w-5 h-5 animate-spin text-muted-foreground" />
          )}
          {!loading && logs.length === 0 && (
            <p className="text-sm text-muted-foreground">No outreach activity yet.</p>
          )}
          {logs.map((log) => (
            <div
              key={log.id}
              className="p-3 rounded-md border border-border/50 bg-muted/20"
              data-testid={`log-${log.id}`}
            >
              <div className="flex items-center justify-between gap-2 mb-1.5">
                <div className="flex items-center gap-2 min-w-0">
                  <Badge variant="outline" className="text-[10px] uppercase">
                    {log.intent}
                  </Badge>
                  <span className="text-sm font-medium truncate">
                    {log.lead_name || log.lead_phone}
                  </span>
                </div>
                <Badge
                  className="shrink-0"
                  variant={log.status === 'replied' ? 'default' : 'outline'}
                >
                  {log.status}
                </Badge>
              </div>
              <p className="text-xs text-muted-foreground line-clamp-3">{log.message}</p>
              <p className="text-[10px] text-muted-foreground mt-1.5">
                {new Date(log.created_at).toLocaleString()}
              </p>
            </div>
          ))}
        </CardContent>
      </Card>
    </div>
  );
};

export default OutreachActivityLogsPage;
